import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Spinner from '../../components/Spinner/Spinner';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { shouldRenderQuestion } from '../../helpers/shouldRenderKycQuestion';
import { getPath } from '../../helpers/url';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import { useFieldValueClearer } from '../../hooks/useFieldValueClearer';
import useGetKycService from '../../hooks/useGetKycService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectAllKycFieldStates } from '../../store/applicationform/applicationFormSelectors';
import { IFieldState } from '../../store/applicationform/applicationFormTypes';
import { WizardPageAction } from '../../store/wizard/wizard';
import { Kyc, Question } from '../../types/KycTypes';
import KycQuestion from '../KycPage/KycQuestion';

const getQuestionFieldName = (question: Question) => 'KYC_' + question.groupId + '_' + question.name;

const useRequiredPepFields = (
    kycData: Kyc | undefined,
    pepAnswerFieldStates: IFieldState[],
    currentPage: number,
): string[] => {
    // Figure out required visible fields
    return useMemo(() => {
        const pageIndexQuestions = kycData?.groups?.[0]?.questions ?? [];
        const visibleQuestions =
            pageIndexQuestions.filter((question) =>
                shouldRenderQuestion(question, pageIndexQuestions, pepAnswerFieldStates),
            ) ?? [];

        return visibleQuestions.map(getQuestionFieldName);
    }, [kycData, pepAnswerFieldStates, currentPage]);
};

const PepPage = () => {
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { kycData, allQuestions, isError, isLoading } = useGetKycService(true);
    const { currentPage, pageAction, setCurrentPage } = usePagesContext();
    const { countryCode, languageCode } = useSettings();
    const pepAnswerFieldStates = useAppSelector(selectAllKycFieldStates)();
    const navigate = useNavigate();
    useComponentTracker('PepPage');
    useFieldValueClearer(kycData, pepAnswerFieldStates);
    const defaultPagesAction = useDefaultPagesAction();
    const pageIndexQuestions = kycData?.groups?.[0]?.questions ?? [];
    const visibleQuestions =
        pageIndexQuestions.filter((question) =>
            shouldRenderQuestion(question, pageIndexQuestions, pepAnswerFieldStates),
        ) ?? [];

    useSetBannerType('bannerOne');

    const requiredFields = useRequiredPepFields(kycData, pepAnswerFieldStates, currentPage);
    const validationErrors = useValidation(requiredFields, visibleQuestions);

    // If there are no questions to display, go to the next page
    useEffect(() => {
        //If we get error from getKnowYourCustomerService, navigate to errorPage
        if (isError) {
            navigate(getPath(ConsumerLoanTargetPath.error, languageCode, countryCode));
        }
        if (pageAction === 'back' && kycData?.groups && kycData?.groups.length === 0) {
            setCurrentPage(currentPage - 1);
        }

        if (pageAction === 'next' && kycData?.groups && kycData?.groups.length === 0) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, kycData, isError]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next') {
            if (isError) {
                return false;
            }

            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
        }
        return defaultPagesAction(action);
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            {isLoading ? (
                <Spinner size="xl" />
            ) : (
                kycData?.groups.map((pep, index) => (
                    <div key={index} style={{ marginBottom: '45px' }}>
                        <Title text={pep?.title} />
                        <ShowDetailsModal
                            label={translate('dialog.pep.label')}
                            dialogTitle={translate('dialog.pep.label')}
                            dialogContent={translate('dialog.pep.content')}
                        />
                        {pep.questions
                            .filter((question) => shouldRenderQuestion(question, allQuestions, pepAnswerFieldStates))
                            .map((question, i) => {
                                return (
                                    <div key={i}>
                                        <Subtitle text={question.label} />
                                        <KycQuestion kycData={kycData} question={question} />
                                    </div>
                                );
                            })}
                    </div>
                ))
            )}

            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default PepPage;
